import React, { useCallback, useEffect, useState } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";
import { apiClient } from "../../api/client";
import { useAppSelector } from "../../store/hooks";
import { useConfirm } from "../../ui/components/ConfirmDialog";

type Device = {
  deviceId: string;
  shortId?: string;
  status: string;
  name?: string | null;
  panelProfile?: string;
  layoutRevision?: number;
  lastSeen?: string | null;
  firmwareVersion?: string | null;
  claimedAt?: string;
};

const Wrap = styled.div`
  min-height: calc(100vh - 64px);
  background: #f0f0f0;
  padding: 1.25rem;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New",
    monospace;
`;

const Card = styled.div`
  max-width: 960px;
  margin: 0 auto;
  background: #ffffff;
  border: 1px solid #232323;
  box-shadow: 6px 6px #232323;
  padding: 1.25rem;
`;

const BackLink = styled(Link)`
  display: inline-block;
  margin-bottom: 0.75rem;
  color: #111;
  font-weight: 700;
  text-decoration: underline;
`;

const StatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 0.5rem 1rem;
  font-size: 0.85rem;
  margin-bottom: 1rem;
  line-height: 1.4;
`;

const StatLabel = styled.span`
  color: #5f5f5f;
  display: block;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const SectionTitle = styled.h3`
  margin: 1.25rem 0 0.5rem;
  font-size: 1rem;
`;

const Actions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-top: 0.5rem;
`;

const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  align-items: center;
`;

const Input = styled.input`
  border: 2px solid #111;
  padding: 0.45rem 0.6rem;
  font-family: inherit;
  min-width: 200px;
  flex: 1;
`;

const Btn = styled.button`
  border: 2px solid #111;
  padding: 0.45rem 0.75rem;
  background: #ffd24a;
  color: #111;
  font-family: inherit;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 3px 3px 0 #111;
  &:hover {
    transform: translate(-1px, -1px);
    box-shadow: 4px 4px 0 #111;
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const DangerBtn = styled(Btn)`
  background: #fff;
  color: #a30000;
`;

const TokenBox = styled.div`
  border: 2px dashed #111;
  padding: 0.75rem;
  background: #fffef0;
  margin-top: 0.5rem;
  font-size: 0.8rem;
`;

const TokenPre = styled.pre`
  margin: 0.5rem 0 0;
  white-space: pre-wrap;
  word-break: break-all;
  user-select: all;
`;

const Err = styled.p`
  color: #a30000;
  font-weight: 600;
  margin: 0 0 0.75rem;
`;

function parseApiError(e: unknown): string {
  if (e && typeof e === "object" && "response" in e) {
    const r = (e as { response?: { data?: unknown } }).response;
    const data = r?.data;
    if (data && typeof data === "object" && data !== null && "error" in data) {
      return String((data as { error: unknown }).error);
    }
  }
  if (e instanceof Error) return e.message;
  return "request failed";
}

const AotdIrlMyFrameDetail: React.FC = () => {
  const auth = useAppSelector((s) => s.auth);
  const { deviceId: rawDeviceId = "" } = useParams<{ deviceId: string }>();
  const deviceId = rawDeviceId ? decodeURIComponent(rawDeviceId) : "";
  const navigate = useNavigate();
  const confirm = useConfirm();

  const [loading, setLoading] = useState(true);
  const [device, setDevice] = useState<Device | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [issuedToken, setIssuedToken] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const loadAll = useCallback(async () => {
    setError(null);
    const listRes = await apiClient.get<{ devices?: Device[] }>("/api/aotd-irl/devices");
    const found = (listRes.data?.devices ?? []).find((d) => d.deviceId === deviceId) ?? null;
    setDevice(found);
    setName(found?.name ?? "");
  }, [deviceId]);

  useEffect(() => {
    setIssuedToken(null);
  }, [deviceId]);

  useEffect(() => {
    if (!deviceId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        await loadAll();
      } catch (e: unknown) {
        if (!cancelled) setError(parseApiError(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [deviceId, loadAll]);

  const onRename = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      await apiClient.patch(`/api/aotd-irl/devices/${encodeURIComponent(deviceId)}/config`, { name });
      await loadAll();
    } catch (e: unknown) {
      setError(parseApiError(e));
    } finally {
      setBusy(false);
    }
  }, [deviceId, name, loadAll]);

  const onRegenerate = useCallback(async () => {
    const ok = await confirm({
      title: "Regenerate device token?",
      message:
        "The frame will need to be re-paired, or will receive the new token via TOFU on next sync.",
      confirmLabel: "Regenerate",
      tone: "danger",
    });
    if (!ok) return;
    setBusy(true);
    setError(null);
    try {
      const res = await apiClient.post<{ issuedToken?: string }>(
        `/api/aotd-irl/devices/${encodeURIComponent(deviceId)}/regenerate-token`
      );
      setIssuedToken(res.data?.issuedToken ?? null);
    } catch (e: unknown) {
      setError(parseApiError(e));
    } finally {
      setBusy(false);
    }
  }, [deviceId, confirm]);

  const onUnclaim = useCallback(async () => {
    const ok = await confirm({
      title: "Unclaim this frame?",
      message:
        "You'll permanently lose access to this frame. The device will revert to its un-paired state and will need to be claimed again before it can show your art.",
      confirmLabel: "Unclaim",
      tone: "danger",
    });
    if (!ok) return;
    setBusy(true);
    setError(null);
    try {
      await apiClient.delete(`/api/aotd-irl/devices/${encodeURIComponent(deviceId)}`);
      navigate("/aotd-irl/my");
    } catch (e: unknown) {
      setError(parseApiError(e));
    } finally {
      setBusy(false);
    }
  }, [deviceId, navigate, confirm]);

  const copyToken = useCallback(() => {
    if (!issuedToken) return;
    void navigator.clipboard.writeText(issuedToken);
  }, [issuedToken]);

  if (auth.bootstrapStatus !== "ready") {
    return null;
  }
  if (!auth.token) {
    return <Navigate to="/login" replace />;
  }

  if (!deviceId) {
    return (
      <Wrap>
        <Card>
          <p>Invalid frame id.</p>
          <BackLink to="/aotd-irl/my">← Back to my frames</BackLink>
        </Card>
      </Wrap>
    );
  }

  if (loading) {
    return (
      <Wrap>
        <Card>
          <p style={{ margin: 0 }}>Loading…</p>
        </Card>
      </Wrap>
    );
  }

  if (!device) {
    return (
      <Wrap>
        <Card>
          <Err style={{ marginBottom: "0.5rem" }}>Frame not found or you don&apos;t have access.</Err>
          <BackLink to="/aotd-irl/my">← Back to my frames</BackLink>
        </Card>
      </Wrap>
    );
  }

  const shortLabel = device.shortId ?? device.deviceId.slice(-4).toUpperCase();

  return (
    <Wrap>
      <Card>
        <BackLink to="/aotd-irl/my">← My frames</BackLink>
        <h2 style={{ margin: "0.25rem 0 0", fontSize: "1.35rem" }}>[{shortLabel}]</h2>
        <p style={{ margin: "0.35rem 0 0", color: "#5f5f5f", fontSize: "0.9rem" }}>
          {device.name || "Unnamed frame"}
        </p>

        {error && <Err>{error}</Err>}

        <StatGrid>
          <div>
            <StatLabel>short id</StatLabel>
            [{shortLabel}]
          </div>
          <div>
            <StatLabel>device id</StatLabel>
            <code style={{ wordBreak: "break-all" }}>{device.deviceId}</code>
          </div>
          <div>
            <StatLabel>panel</StatLabel>
            {device.panelProfile ?? "—"}
          </div>
          <div>
            <StatLabel>layout rev</StatLabel>
            {device.layoutRevision ?? "—"}
          </div>
          <div>
            <StatLabel>firmware</StatLabel>
            {device.firmwareVersion ?? "—"}
          </div>
          <div>
            <StatLabel>claimed</StatLabel>
            {device.claimedAt ? new Date(device.claimedAt).toLocaleString() : "—"}
          </div>
          <div>
            <StatLabel>last seen</StatLabel>
            {device.lastSeen ? new Date(device.lastSeen).toLocaleString() : "—"}
          </div>
        </StatGrid>

        <SectionTitle>Manage</SectionTitle>
        <Actions>
          <Row>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Friendly name"
              aria-label="Frame name"
            />
            <Btn type="button" disabled={busy} onClick={() => void onRename()}>
              Save name
            </Btn>
          </Row>
          <Row>
            <Btn type="button" disabled={busy} onClick={() => void onRegenerate()}>
              Regenerate token
            </Btn>
          </Row>
          {issuedToken && (
            <TokenBox>
              <strong>New device token</strong> — copy now; it won&apos;t be shown again.
              <TokenPre>{issuedToken}</TokenPre>
              <Btn type="button" onClick={copyToken}>
                Copy to clipboard
              </Btn>
            </TokenBox>
          )}
          <Row>
            <DangerBtn type="button" disabled={busy} onClick={() => void onUnclaim()}>
              Unclaim frame
            </DangerBtn>
          </Row>
        </Actions>
      </Card>
    </Wrap>
  );
};

export default AotdIrlMyFrameDetail;
